/**
 * DescriptiveAnalysis — schema view for `DescriptiveAnswerV1`: a written
 * answer in a descriptive subject, judged on one axis, part by part.
 * Composes:
 *   - VerdictBlock      (the headline line)
 *   - the assessment    (what was right, what was missing — on `accuracyRationale`, inside the VerdictBlock)
 *   - AnswerPartsList   (the question's parts, each with its mark and note)
 *   - DiagnosisBlock    (admin audience only)
 *   - HintsList         (`next` — one line, what to revise)
 *
 * No steps, no rationales, no suggestions: the schema has none. See
 * docs/FeatureDesigns/solution-analysis/descriptive-analysis-target.md.
 */
import type { Feedback } from '@padcanvas/shared';
import { VerdictBlock } from '../sections/VerdictBlock';
import { AnswerPartsList } from '../sections/AnswerPartsList';
import { DiagnosisBlock } from '../sections/DiagnosisBlock';
import { diagnosisForStudent } from '../sections/diagnosisLabels';
import { HintsList } from '../sections/HintsList';
import { AdminOnlySection } from '../sections/AdminOnlySection';
import type { SolutionAnalysisAudience } from '../audience';

interface Props {
    feedback: Feedback;
    audience?: SolutionAnalysisAudience;
}

export function DescriptiveAnalysis({ feedback, audience = 'admin' }: Props) {
    const a = feedback.llmAssessment;
    return (
        <>
            <VerdictBlock verdict={a?.verdict} assessment={a?.accuracyRationale} />
            <AnswerPartsList parts={a?.answerParts} />
            {/* Mounted only with a diagnosis to show: the heading is unconditional,
                and a withheld diagnosis (selection gate) would leave it bare. */}
            {audience === 'admin' && diagnosisForStudent(a?.diagnosis) && (
                <AdminOnlySection title="Diagnosis">
                    <DiagnosisBlock diagnosis={a?.diagnosis} />
                </AdminOnlySection>
            )}
            <HintsList items={a?.solutionGuidance} title="What to look at next" />
        </>
    );
}
